import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  useParams,
} from "react-router-dom";
import Header from "./components/Header";
import Home from "./pages/clients/Home";
import Login from "./pages/Authentications/Login";
import Register from "./pages/Authentications/Register";
import ForgotPassword from "./pages/Authentications/ForgotPassword";
import ResetPassword from "./pages/Authentications/ResetPassword";
import EmailVerification from "./pages/Authentications/EmailVerification";
import NotFound from "./pages/NotFound";
import { useAuth } from "./context/AuthContext";
import Footer from "./components/Footer";
import NewsDetail from "./components/NewsDetail";
import Article from "./pages/member/Article";
import CreateArticle from "./pages/member/CreateArticle";
import EditArticle from "./pages/member/EditArticle";
import Service from "./pages/member/Service";
import History from "./pages/member/History";
import Dashboard from "./pages/member/Dashboard";
import Dashboardadmin from "./pages/member/Dashboardadmin";
import DashboardModerator from "./pages/member/DashboardModerator";
import Users from "./pages/member/Users";
import Cencor from "./pages/member/Cencor";
import Statistical from "./pages/member/Statistical";
import AdminLogs from "./pages/member/AdminLogs";
import Terms from "./pages/member/Terms";
import PrivacyPolicy from "./pages/member/PrivacyPolicy";
import Categories from "./pages/member/Categories";
import BotModerator from "./pages/member/BotModerator";
import News from "./pages/clients/News";
import HistoryUser from "./pages/member/HistoryUser";
import Trash from "./pages/member/Trash";
// import UserProfile from "./pages/clients/UserProfile";
import DetailArticle from "./pages/member/DetailArticle";
interface ProtectedRouteProps {
  children: React.ReactNode;
  fallbackPath?: string;
}

// Component để redirect từ /news/:slug sang /:slug
const NewsSlugRedirect: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/${slug}`} replace />;
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  fallbackPath = "/dang-nhap" 
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Hiển thị loading nếu đang load auth state
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Đang tải...
      </div>
    );
  }

  // Nếu chưa đăng nhập
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} replace />;
  }

  return children as React.ReactElement;
};

const App: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const location = useLocation();

  // Các route không cần đăng nhập
  const publicRoutes = [
    "/dang-nhap",
    "/dang-ky", 
    "/quen-mat-khau",
    "/dat-lai-mat-khau",
    "/xac-thuc-email"
  ];

  const isPublicRoute = publicRoutes.includes(location.pathname);
  const shouldShowHeader = !isPublicRoute && 
    !location.pathname.startsWith("/admin") && 
    !location.pathname.startsWith("/member") &&
    !location.pathname.startsWith("/dashboard") &&
    !location.pathname.startsWith("/posts") &&
    !location.pathname.startsWith("/service") &&
    !location.pathname.startsWith("/transactions") &&
    !location.pathname.startsWith("/moderator");
  const shouldShowFooter = !isPublicRoute && 
    !location.pathname.startsWith("/admin") && 
    !location.pathname.startsWith("/member") &&
    !location.pathname.startsWith("/dashboard") &&
    !location.pathname.startsWith("/posts") &&
    !location.pathname.startsWith("/service") &&
    !location.pathname.startsWith("/transactions") &&
    !location.pathname.startsWith("/moderator");

  return (
    <>
      <main>
        {shouldShowHeader && <Header isAuthenticated={isAuthenticated} onLogout={logout} />}
        <Routes>
          {/* Public Routes - Không cần đăng nhập */}
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/news" element={<News />} />
          <Route path="/clients/news" element={<Navigate to="/news" replace />} />
          <Route path="/clients/home" element={<Navigate to="/" replace />} />
          <Route path="/client/premium/home" element={<Home />} />
          <Route path="/news/:slug" element={<NewsSlugRedirect />} />
          {/* <Route path="/profile/:id" element={<UserProfile />} /> */}
          <Route path="/dang-nhap" element={<Login />} />
          <Route path="/dang-ky" element={<Register />} />
          <Route path="/quen-mat-khau" element={<ForgotPassword />} />
          <Route path="/dat-lai-mat-khau" element={<ResetPassword />} />
          <Route path="/xac-thuc-email" element={<EmailVerification />} />

          {/* Protected Routes - Cần đăng nhập */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/posts/my-posts"
            element={
              <ProtectedRoute>
                <Article />
              </ProtectedRoute>
            }
          />
          <Route
            path="/posts/create"
            element={
              <ProtectedRoute>
                <CreateArticle />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/edit-article/:id"
            element={
              <ProtectedRoute>
                <EditArticle />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service"
            element={
              <ProtectedRoute>
                <Service />
              </ProtectedRoute>
            }
          />
          <Route
            path="/moderator/DashboardModerator"
            element={
              <ProtectedRoute>
                <DashboardModerator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/posts/detail/:id"
            element={
              <ProtectedRoute>
                <DetailArticle />
              </ProtectedRoute>
            }
          />
          <Route
            path="/moderator/pending-posts"
            element={
              <ProtectedRoute>
                <Cencor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <Dashboardadmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/statistics"
            element={
              <ProtectedRoute>
                <Statistical />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/terms"
            element={
              <ProtectedRoute>
                <Terms />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/privacy-policy"
            element={
              <ProtectedRoute>
                <PrivacyPolicy />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/categories"
            element={
              <ProtectedRoute>
                <Categories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/moderation/terms"
            element={
              <ProtectedRoute>
                <BotModerator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/historyuser"
            element={
              <ProtectedRoute>
                <HistoryUser />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/logs"
            element={
              <ProtectedRoute>
                <AdminLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/transactions"
            element={
              <ProtectedRoute>
                <History />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/trash"
            element={
              <ProtectedRoute>
                <Trash />
              </ProtectedRoute>
            }
          />

          <Route path="/member/dashboard" element={<Navigate to="/dashboard" replace />} />
          <Route path="/member/article" element={<Navigate to="/posts/my-posts" replace />} />
          <Route path="/member/create-article" element={<Navigate to="/posts/create" replace />} />
          <Route path="/member/service" element={<Navigate to="/service" replace />} />
          <Route path="/member/history" element={<Navigate to="/transactions" replace />} />
          <Route path="/member/cencor" element={<Navigate to="/moderator/pending-posts" replace />} />
          <Route path="/member/statistical" element={<Navigate to="/admin/statistics" replace />} />
          <Route path="/member/users" element={<Navigate to="/admin/users" replace />} />
          <Route path="/member/dashboardadmin" element={<Navigate to="/moderator/deleted-posts" replace />} />

          <Route path="/dang-nhap" element={<Login />} />
          <Route path="/dang-ky" element={<Register />} />
          <Route path="/quen-mat-khau" element={<ForgotPassword />} />
          <Route path="/dat-lai-mat-khau" element={<ResetPassword />} />
          
          {/* Route cho chi tiết bài viết - đặt trước route * để match với slug */}
          <Route path="/:slug" element={<NewsDetail />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {shouldShowFooter && <Footer />}
    </>
  );
};

const AppWrapper: React.FC = () => (
  <Router>
    <ScrollToTop />
    <App />
  </Router>
);

// Component để scroll về đầu trang khi route thay đổi
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

export default AppWrapper;